import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ReviewerStatus, ReviewLifecycleActionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import openAICheckerPanel from 'notebook/ai-tools/openAICheckerPanel';
import {ReviewBadgeContext, ReviewErrorType} from 'notebook/reviews/ReviewBadgeContext';
import * as ReviewDAO from 'notebook/reviews/ReviewDAO';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import EntityStore from 'stores/EntityStore';
import {FONT_PROXIMA_12, FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {multiLineEllipsisText} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import Avatar, {HasAvatar} from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, GRAY_SEMIDARK} from 'ui-library/Colors';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import APIUtil from 'util/APIUtil';
import * as DateUtil from 'util/DateUtil';

import {mapReviewCriteriaGroupsToProps} from '../reduceReviewCriteria';
import {ReviewAction} from '../ReviewConstants';
import {ReviewCriteriaGroup} from '../ReviewCriteriaConstants';
import {ReviewerReassignmentModal} from '../ReviewerReassignmentModal';
import ReviewStatusModal, {ReviewStageInfo} from '../ReviewStatusModal';
import {REVIEW_ACTION_TO_DISPLAY_PAST_TENSE} from './ReviewLifecycleConstants';

export const BLOCKED = 'Not available yet';
export const PENDING = 'Pending';
export const REJECT = 'Reject';
export const REJECTED = 'Rejected';
export const REASSIGNMENT_REQUIRED = 'Reassignment required';
export const REASSIGN = 'Reassign';
export const NO_REVIEWER_AVAILABLE = 'No reviewer available';

export const SENT_FOR_REVIEW = 'Sent for review';

export interface ReviewLifecycleSummaryItemProps {
  canReassign: boolean;
  canAccept: boolean;
  canReject: boolean;
  canReview: boolean;
  user: ReviewLifecycleSummaryAvatar;
  isUserValidReviewer: boolean;
  hasValidReviewerReplacements?: boolean;
  reviewStageAction: string;
  stageStatus: ReviewerStatus;
  stageInfo?: ReviewStageInfo;
  completedTime: string | null;
  authors: Array<MinimalEntity> | undefined;
  wasEsigned: boolean;
  // TODO (BNCH-92610) Refactor this to not be optional
  reviewLifecycleAction?: ReviewLifecycleActionType;
  message: string | undefined | null;
  reviewSenderId: string | undefined;
}

interface ReviewerActionButtonsProps {
  action: string;
  canAccept: boolean;
  canReject: boolean;
  reviewCriteriaGroups: Array<ReviewCriteriaGroup>;
  stageInfo?: ReviewStageInfo;
  reviewLifecycleAction?: ReviewLifecycleActionType;
}

interface StageStatusTextProps {
  stageStatus: ReviewerStatus;
  readableTime: string | null;
  wasEsigned: boolean;
  isUserValidReviewer: boolean;
  action?: ReviewLifecycleActionType;
}

export type ReviewLifecycleSummaryAvatar = HasAvatar & {id: string};

function mapStateToProps(
  state: BenchlingState,
  {reviewableId}: {reviewableId: string}
): {reviewCriteriaGroups: Array<ReviewCriteriaGroup>} {
  if (!BenchlingConfig.ENABLE_ELN_REVIEW_CRITERIA) {
    return {
      reviewCriteriaGroups: [],
    };
  }
  return {
    reviewCriteriaGroups: mapReviewCriteriaGroupsToProps(state, reviewableId) ?? [],
  };
}

interface ConnectedProps {
  reviewCriteriaGroups: Array<ReviewCriteriaGroup>;
}

function StageStatusText({
  stageStatus,
  readableTime,
  wasEsigned,
  isUserValidReviewer,
  action,
}: StageStatusTextProps): JSX.Element | null {
  switch (stageStatus) {
    case ReviewerStatus.FINISHED:
    case ReviewerStatus.REJECTED:
      return (
        <div>
          {action && (
            <div>
              {stageStatus === ReviewerStatus.REJECTED
                ? REJECTED
                : REVIEW_ACTION_TO_DISPLAY_PAST_TENSE[action]}{' '}
              on
            </div>
          )}
          <div className={css(styles.centerFlexRow)}>
            <div className={css(styles.time)}>{readableTime}</div>
            {wasEsigned && (
              <Tooltip text="Electronically Signed">
                <BenchlingIcon className={css(styles.esign)} icon="esign" />
              </Tooltip>
            )}
          </div>
        </div>
      );
    case ReviewerStatus.PENDING:
      return (
        <div className={css(styles.statusText)}>{isUserValidReviewer ? PENDING : REASSIGNMENT_REQUIRED}</div>
      );
    case ReviewerStatus.BLOCKED:
      return (
        <div className={css(styles.statusText)}>{isUserValidReviewer ? BLOCKED : REASSIGNMENT_REQUIRED}</div>
      );
    default:
      assert(false, `Received an unexpected stage status: ${stageStatus}`);
  }
}

function onReassignClick(
  reviewableId: string,
  stageId: string,
  authors: Array<MinimalEntity> | undefined,
  reviewSenderId: string | undefined
): void {
  legacyRunModal(ReviewerReassignmentModal, {
    reviewLifecycleStageId: stageId,
    onRequestClose: () => {},
    onRequestSubmit: async ({selectedReviewerId}: {selectedReviewerId: string}) => {
      await ReviewDAO.reassignRLCReviewer({
        fileId: reviewableId,
        reviewLifecycleStageId: stageId,
        newUserId: selectedReviewerId,
      });
    },
    authors,
    reviewSenderId,
    reviewableId,
  });
}

interface ReviewerNameWithReassignProps {
  isStageReassignable: boolean;
  stageId: string | undefined;
  authors: Array<MinimalEntity> | undefined;
  user: ReviewLifecycleSummaryAvatar;
  reviewSenderId: string | undefined;
}

function ReviewerNameWithReassign({
  isStageReassignable,
  stageId,
  authors,
  user,
  reviewSenderId,
}: ReviewerNameWithReassignProps): JSX.Element {
  const {reviewableId} = ReviewBadgeContext.useContext();
  const name = EntityStore.currentUserId === user.id ? 'You' : user.name;
  const {setIsDropdownOpen} = ReviewBadgeContext.useContext();
  return (
    <div
      className={css(styles.centerFlexRow, styles.reviewerName)}
      {...getTestProps({component: 'ReviewerNameWithReassign', element: 'root'})}
    >
      <Avatar entity={user} size={AvatarSize.XSMALL} />
      <div
        className={css(styles.name)}
        {...getTestProps({component: 'ReviewerNameWithReassign', element: 'name'})}
      >
        {name}
      </div>
      {isStageReassignable && stageId && (
        <Tooltip text="Reassign reviewer">
          <Button
            buttonStyle={ButtonStyle.UNBUTTON}
            className={css(styles.reassign)}
            icon="assign"
            onClick={() => {
              assert(reviewableId != null, 'No ID for reviewable provided');
              setIsDropdownOpen(false);
              onReassignClick(reviewableId, stageId, authors, reviewSenderId);
            }}
            {...getTestProps({component: 'ReviewerReassignButton', element: 'root'})}
          />
        </Tooltip>
      )}
    </div>
  );
}

function ReviewerActionButtons({
  action,
  canAccept,
  canReject,
  reviewCriteriaGroups,
  stageInfo,
  reviewLifecycleAction,
}: ReviewerActionButtonsProps): JSX.Element {
  const {
    setIsDropdownOpen,
    onReviewChange,
    reviewError,
    performanceMetadata,
    reviewableId,
    reviewId,
    renderAdditionalReviewInfo,
  } = ReviewBadgeContext.useContext();
  assert(reviewableId != null, 'No ID for reviewable provided');
  assert(reviewId != null, 'No ID for review provided');

  // TODO (BNCH-141517): Add validation to block reviewer ACCEPT actions when there are unsubmitted tables.
  // This should check reviewError?.type === ReviewErrorType.TOOLTIP and pass ReviewAction.ACCEPT to the
  // error function, similar to how PrimaryReviewAction handles validation in ReviewLifecycleSummaryPanel.

  return (
    <div>
      <div className={css(styles.actionButtons)}>
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          text={action}
          className={css(styles.button, styles.centerFlexRow, styles.actionText)}
          size={ButtonSize.MINI}
          disabled={!canAccept}
          onClick={() => {
            setIsDropdownOpen(false);
            ReviewStatusModal.open({
              action: ReviewAction.ACCEPT,
              onUpdate: onReviewChange,
              fileId: reviewableId,
              reviewId,
              reviewCriteriaGroups,
              stageInfo,
              isForReviewLifecycle: true,
              // TODO (BNCH-92610) Refactor this to just use reviewLifecycleAction
              message: ReviewUtil.getDefaultReviewComment(reviewLifecycleAction ?? ReviewAction.ACCEPT, true),
              title: ReviewUtil.getReviewLifecycleActionTitle(
                reviewLifecycleAction ?? ReviewLifecycleActionType.ACCEPT
              ),
              renderAdditionalReviewInfo,
              renderReviewError:
                reviewError?.type === ReviewErrorType.CALLBACK ? reviewError.error : undefined,
              performanceMetadata,
            });
          }}
          dataTestAttrs={{component: 'ReviewerActionButtons', element: 'reviewStageApproveButton'}}
        />
        <Button
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          className={css(styles.button, styles.centerFlexRow, styles.actionText)}
          text={REJECT}
          size={ButtonSize.MINI}
          disabled={!canReject}
          onClick={() => {
            setIsDropdownOpen(false);
            ReviewStatusModal.open({
              action: ReviewAction.REJECT,
              onUpdate: onReviewChange,
              fileId: reviewableId,
              reviewId,
              reviewCriteriaGroups,
              stageInfo,
              isForReviewLifecycle: true,
              message: ReviewUtil.getDefaultReviewComment(ReviewAction.REJECT, true),
              renderAdditionalReviewInfo,
              renderReviewError:
                reviewError?.type === ReviewErrorType.CALLBACK ? reviewError.error : undefined,
              performanceMetadata,
            });
          }}
        />
      </div>
      {BenchlingConfig.ELN_ENABLE_AI_CHECKER && APIUtil.isEntry(reviewableId) && (
        <div className={css(styles.checkUsingAIButton)}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className={css(styles.button, styles.centerFlexRow, styles.actionText)}
            text="Check entry"
            size={ButtonSize.MINI}
            onClick={() => {
              openAICheckerPanel(reviewableId, 'reviewer-action-buttons');
            }}
          />
        </div>
      )}
    </div>
  );
}

function isStageCompleted(
  stageStatus: ReviewerStatus
): stageStatus is ReviewerStatus.FINISHED | ReviewerStatus.REJECTED {
  return stageStatus === ReviewerStatus.FINISHED || stageStatus === ReviewerStatus.REJECTED;
}

export function ReviewLifecycleSummaryItem(
  props: ReviewLifecycleSummaryItemProps & ConnectedProps
): JSX.Element {
  const {
    user,
    isUserValidReviewer,
    canReassign,
    canAccept,
    canReject,
    canReview,
    stageStatus,
    stageInfo,
    reviewStageAction,
    completedTime,
    reviewCriteriaGroups,
    authors,
    wasEsigned,
    reviewLifecycleAction,
    message,
    reviewSenderId,
  } = props;

  const [showMore, setShowMore] = useState(false);
  const [isMessageOverflowing, setIsMessageOverflowing] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);

  const messageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
    } else if (messageRef.current) {
      setIsMessageOverflowing(messageRef.current.scrollHeight > messageRef.current.clientHeight);
    }
  }, [message, isInitialRender]);

  const isStageReassignable = canReassign && !isStageCompleted(stageStatus);
  const readableTime = completedTime
    ? DateUtil.readableTime(new Date(completedTime), {
        showYear: true,
        showTime: true,
      })
    : null;

  const showActionButtons =
    stageStatus === ReviewerStatus.PENDING && canReview && stageInfo?.id && isUserValidReviewer;

  return (
    <div>
      <ReviewerNameWithReassign
        isStageReassignable={isStageReassignable}
        stageId={stageInfo?.id}
        authors={authors}
        user={user}
        reviewSenderId={reviewSenderId}
      />
      {!showActionButtons && (
        <StageStatusText
          {...getTestProps({component: 'StageStatusText', element: 'root'})}
          stageStatus={stageStatus}
          readableTime={readableTime}
          wasEsigned={wasEsigned}
          isUserValidReviewer={isUserValidReviewer}
          action={reviewLifecycleAction}
        />
      )}
      {message && (
        <div
          {...getTestProps({component: 'MessageContainer', element: 'root'})}
          className={css(styles.messageContainer)}
          onClick={
            isMessageOverflowing
              ? (e) => {
                  e.stopPropagation();
                  setShowMore((prevShowMore) => !prevShowMore);
                }
              : undefined
          }
        >
          <div
            {...getTestProps({component: 'MessageText', element: 'root'})}
            ref={messageRef}
            className={css(showMore ? styles.message : styles.messageShowLess)}
          >
            {message}
          </div>
          {isMessageOverflowing ? (
            <div
              {...getTestProps({component: 'ShowMoreLessButton', element: 'root'})}
              className={css(styles.showMoreLessButton)}
            >
              {showMore ? 'Show less' : 'Show more'}
            </div>
          ) : (
            <div className={css(styles.messageBottomSpacer)} />
          )}
        </div>
      )}
      {showActionButtons && (
        <ReviewerActionButtons
          action={reviewStageAction}
          canAccept={canAccept}
          canReject={canReject}
          reviewCriteriaGroups={reviewCriteriaGroups}
          stageInfo={{id: stageInfo.id, name: stageInfo.name, description: stageInfo.description}}
          reviewLifecycleAction={reviewLifecycleAction}
        />
      )}
    </div>
  );
}

const message = {
  paddingLeft: 8,
  paddingRight: 8,
  marginTop: 8,
};

export const styles = StyleSheet.create({
  messageContainer: {
    backgroundColor: Color.GRAY_100,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    borderRadius: 4,
  },
  message: {
    ...message,
    overflow: 'hidden',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
  },
  messageShowLess: {
    ...message,
    ...multiLineEllipsisText(3),
  },
  showMoreLessButton: {
    cursor: 'pointer',
    color: Color.BLUE_500,
    paddingLeft: 8,
    paddingBottom: 8,
  },
  messageBottomSpacer: {
    marginBottom: 4,
  },
  title: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  button: {
    padding: '8px 12px',
  },
  reviewStage: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  actionText: {
    ...FONT_PROXIMA_12,
    // ensure centering of the action text in button
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  statusText: {
    ...FONT_PROXIMA_12,
    color: GRAY_SEMIDARK,
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    // For hyphenated review actions, prevent line break.
    whiteSpace: 'nowrap',
    justifyContent: 'space-between', // Adjust spacing between buttons
    gap: '6px', // Adds consistent spacing between buttons
  },
  checkUsingAIButton: {
    margin: '6px 0px',
  },
  textBox: {
    margin: '6px 0px',
  },
  time: {
    ...FONT_PROXIMA_12,
    color: GRAY_SEMIDARK,
  },
  name: {
    ...FONT_PROXIMA_13,
    marginLeft: '6px',
  },
  centerFlexRow: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    width: '100%',
  },
  reviewerName: {
    marginBottom: '6px',
  },
  reassign: {
    color: Color.BLUE_500,
    margin: '0px 4px',
    // Wrapping with tooltip seems to be pushing down button by 4 pixels
    marginTop: '-4px',
  },
  esign: {
    color: Color.GREEN_600,
    width: '25px',
  },
});

export default connect(mapStateToProps)(ReviewLifecycleSummaryItem);
